import { UpdateDigitalDossierGeneralInformationDto } from '../../../../../services/types/query-params';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { useToastsHook, useTranslation } from '@bcba/react-hooks';
import {
  QueryObserverResult,
  RefetchOptions,
  useMutation,
} from '@tanstack/react-query';
import { updateGeneralInformation } from '../../../../../services/digital-dossiers/update-information';
import {
  CompanyActivity,
  CompanyType,
  RucaStatus,
  SisaActivity,
  SisaScoring,
  VALIDATION_STATUSES,
} from '@bcba/types';
import {
  SubmitHandler,
  UseFormHandleSubmit,
  UseFormReturn,
  useForm,
} from '@agrotoken-tech/ui';
import { DigitalDossierGeneralInformationSerializer } from '@bcba/serializers';
import { useDigitalDossierSectionContext } from '../../../context/DigitalDossierSectionContext';
import { useDigitalDossierActionButtons } from '../../../hooks/useDigitalDossierActionButtons';
import { usePendingChangesAlert } from '../../../../../contexts/hooks';

interface Props {
  data: DigitalDossierGeneralInformationSerializer;
  refetch: (
    options?: RefetchOptions | undefined,
  ) => Promise<
    QueryObserverResult<DigitalDossierGeneralInformationSerializer, Error>
  >;
}

const resolver = classValidatorResolver(
  UpdateDigitalDossierGeneralInformationDto,
);

interface UseGeneralInfoForm {
  handleSubmit: UseFormHandleSubmit<
    UpdateDigitalDossierGeneralInformationDto,
    undefined
  >;
  onSubmit: SubmitHandler<UpdateDigitalDossierGeneralInformationDto>;
  formMethods: UseFormReturn<
    UpdateDigitalDossierGeneralInformationDto,
    any,
    undefined
  >;
  isPendingChangeRequest: boolean;
  validationMessage: string;
  companyFiscalId: string;
  name: string;
  companyType?: CompanyType;
  toBeCompletedByMessage: string;
  showForm: boolean;
  mainActivities?: CompanyActivity[];
  activityStartDate?: number;
  activityEndDate?: number;
  rucaStatus?: RucaStatus;
  igjIdentifier?: string;
  sisaScoring?: SisaScoring;
  sisaActivities?: SisaActivity[];
  isPending: boolean;
  isDisabledRequestReview: boolean;
  actionButtons: JSX.Element;
}

export const useGeneralInfoForm = ({
  data,
  refetch,
}: Props): UseGeneralInfoForm => {
  const { t } = useTranslation();

  const { showSuccessToast } = useToastsHook();
  const { refetchDigitalDossierStatus } = useDigitalDossierSectionContext();
  const {
    name,
    companyType,
    companyFiscalId,
    activityStartDate,
    activityEndDate,
    rucaStatus,
    igjIdentifier,
    sisaScoring,
    status,
    validationMessage,
    sisaActivities,
    mainActivities,
  } = data;

  const formMethods = useForm<UpdateDigitalDossierGeneralInformationDto>({
    resolver,
    defaultValues: { mainActivities },
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty },
  } = formMethods;
  usePendingChangesAlert(isDirty);
  const { mutate, isPending } = useMutation({
    mutationFn: updateGeneralInformation,
    onSuccess: () => {
      refetchDigitalDossierStatus();
      showSuccessToast({
        title: t(
          'explorer.digitalDossier.updateGeneralInfo.success.toast.title',
        ),
      });
      refetch().then((d) => {
        if (d.data) {
          reset(
            {
              mainActivities: d.data.mainActivities,
            },
            { keepDirty: false },
          );
        }
      });
    },
  });

  const { actionButtons, isFormReadOnly } = useDigitalDossierActionButtons({
    status,
    isLoadingSave: isPending,
    formHasChanges: isDirty,
  });

  const onSubmit: SubmitHandler<UpdateDigitalDossierGeneralInformationDto> = (
    data: UpdateDigitalDossierGeneralInformationDto,
  ) => {
    mutate(data);
  };

  const toBeCompletedByMessage = t('common.toBeCompletedByBolsa');
  const isPendingChangeRequest =
    status === VALIDATION_STATUSES.PENDING_CHANGE_REQUEST;
  const isDisabledRequestReview = !isDirty;
  const showForm = !isFormReadOnly;

  return {
    handleSubmit,
    onSubmit,
    formMethods,
    isPendingChangeRequest,
    validationMessage,
    companyFiscalId,
    name,
    companyType,
    toBeCompletedByMessage,
    showForm,
    mainActivities,
    activityStartDate,
    activityEndDate,
    rucaStatus,
    igjIdentifier,
    sisaScoring,
    sisaActivities,
    isPending,
    isDisabledRequestReview,
    actionButtons,
  };
};

export default useGeneralInfoForm;
